'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';

import useUser from '@/utils/hooks/swr/useUser';

import { useToggleLayoutPlatform } from '@/utils/zustand/providers/toggle-layout-platform-provider';
import useDarkMode from '@/utils/hooks/utils/useDarkMode';

import LoaderPage from '@/components/base/Loader/LoaderPage';
import { LayoutPlatform } from '@/components/layouts/LayoutPlatform';
import { WrapperPlatformContent } from '@/components/layouts/wrappers/WrapperPlatformContent';
import { AccessControl } from '@/components/middleware/AccessControl';
import { PaddleInitialize } from '@/components/tools/Paddle';

type DashboardLayoutProps = {
    children: React.ReactNode;
};

export default function DashboardLayout({ children }: DashboardLayoutProps) {
    const { useAddDarkMode } = useDarkMode();
    const router = useRouter();
    const { isLoading, userError } = useUser();
    const updatePlatformLayout = useToggleLayoutPlatform((state) => state.updatePlatformLayout);

    useEffect(() => {
        updatePlatformLayout?.(true);
        return () => updatePlatformLayout(false);
    }, [updatePlatformLayout]);

    // Launch theme
    useAddDarkMode();

    useEffect(() => {
        if (userError) {
            router.replace('/');
        }
    }, [router, userError]);

    if (userError || isLoading) {
        return <LoaderPage />;
    }

    return (
        <LayoutPlatform>
            <AccessControl>
                <WrapperPlatformContent>{children}</WrapperPlatformContent>
            </AccessControl>
            <PaddleInitialize />
        </LayoutPlatform>
    );
}
